import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface Channel {
  id: string;
  name: string;
  kind: 'mercado_livre' | 'shopee' | 'shopify' | 'site_proprio' | 'atacado' | 'loja_fisica' | 'amazon' | 'outro';
  color: string;
  icon: string | null;
  is_default: boolean;
}

export const KIND_LABELS: Record<Channel['kind'], string> = {
  mercado_livre: 'Mercado Livre',
  shopee:        'Shopee',
  shopify:       'Shopify',
  site_proprio:  'Site próprio',
  atacado:       'Atacado / B2B',
  loja_fisica:   'Loja física',
  amazon:        'Amazon',
  outro:         'Outro',
};

function translateChannelError(msg: string): string {
  if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('already exists'))
    return 'Já existe um canal com esse nome neste tenant.';
  if (msg.includes('row-level security') || msg.includes('new row violates') || msg.includes('RLS'))
    return 'Permissão insuficiente. Apenas owner e admin podem gerenciar canais.';
  if (msg.includes('foreign key') || msg.includes('violates foreign key'))
    return 'Não é possível excluir este canal pois há dados vinculados a ele.';
  return 'Ocorreu um erro inesperado. Tente novamente.';
}

export { translateChannelError };

export function useChannels() {
  const { tenantId } = useTenant();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    const { data, error } = await supabase
      .from('channels')
      .select('id, name, kind, color, icon, is_default')
      .eq('tenant_id', tenantId)
      .order('is_default', { ascending: false })
      .order('name');
    if (error) {
      setFetchError(translateChannelError(error.message));
    } else {
      setChannels((data as unknown as Channel[]) ?? []);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void fetch(); }, [fetch]);

  async function createChannel(name: string, kind: Channel['kind'], color = '#0f3d2e'): Promise<Channel> {
    if (!tenantId) throw new Error('Tenant não encontrado');
    const { data, error } = await supabase
      .from('channels')
      .insert({ tenant_id: tenantId, name: name.trim(), kind, color } as never)
      .select('id, name, kind, color, icon, is_default')
      .single();
    if (error) throw new Error(translateChannelError(error.message));
    await fetch();
    return data as unknown as Channel;
  }

  async function updateChannel(id: string, patch: Partial<Pick<Channel, 'name' | 'color' | 'kind'>>) {
    const { error } = await supabase.from('channels').update(patch as never).eq('id', id);
    if (error) throw new Error(translateChannelError(error.message));
    await fetch();
  }

  async function setDefaultChannel(id: string) {
    if (!tenantId) throw new Error('Tenant não encontrado');
    // Remove padrão atual
    await supabase.from('channels').update({ is_default: false } as never)
      .eq('tenant_id', tenantId).eq('is_default', true);
    // Define novo padrão
    const { error } = await supabase.from('channels').update({ is_default: true } as never).eq('id', id);
    if (error) throw new Error(translateChannelError(error.message));
    await fetch();
  }

  async function deleteChannel(id: string) {
    const { error } = await supabase.from('channels').delete().eq('id', id);
    if (error) throw new Error(translateChannelError(error.message));
    await fetch();
  }

  const defaultChannel = channels.find((c) => c.is_default) ?? null;

  return {
    channels, loading, fetchError, defaultChannel,
    createChannel, updateChannel, deleteChannel, setDefaultChannel,
    refetch: fetch,
  };
}
